import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, Globe, ChevronDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import logoImage from '../data/d.png';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    // { href: '#home', label: t('nav.home') },
    { href: '#about', label: t('nav.about') },
    { href: '#sectors', label: t('nav.sectors') },
    { href: '#services', label: t('nav.services') },
    { href: '#subsidiaries', label: t('nav.subsidiaries') },
    // { href: '#portfolio', label: t('nav.portfolio') },
    { href: '#contact', label: t('nav.contact') },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-black/95 backdrop-blur-xl border-b border-neon-green/30 shadow-2xl shadow-neon-green/20'
        : 'bg-transparent'
        }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-3 rtl:space-x-reverse"
          >
            <Link to="/" className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="relative">
                <img
                  src={logoImage}
                  alt="Saham Holding - Future Technology Solutions"
                  className="w-12 h-12 lg:w-14 lg:h-14 object-contain drop-shadow-lg"
                />
                <div className="absolute -inset-2 bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 rounded-full blur-lg opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-xl lg:text-2xl gradient-text-neon">
                  {t('nav.brand')}
                </span>
                <span className="text-xs text-neon-green/80 font-medium hidden sm:block">
                  {t('footer.tagline')}
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8 rtl:space-x-reverse">
            {navItems.map((item, index) => (
              <motion.a
                key={index}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className="text-white/90 hover:text-neon-green transition-all duration-300 relative group font-medium"
                whileHover={{ y: -2 }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-neon-green to-neon-cyan group-hover:w-full transition-all duration-300"></span>
                <span className="absolute -bottom-1 left-0 w-0 h-1 bg-gradient-to-r from-neon-green/50 to-neon-cyan/50 group-hover:w-full transition-all duration-500 delay-100 blur-sm"></span>
              </motion.a>
            ))}
          </nav>

          {/* Controls */}
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            {/* Language Toggle */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleLanguage}
              className="relative group p-3 rounded-xl bg-black/30 backdrop-blur-sm border border-neon-green/30 hover:border-neon-green/50 hover:bg-black/50 transition-all duration-300 shadow-lg hover:shadow-neon-green/20"
            >
              <Globe className="w-5 h-5 text-neon-green" />
              <span className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 bg-black/95 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap border border-neon-green/20">
                {language === 'en' ? 'العربية' : 'English'}
              </span>
            </motion.button>

            {/* Mobile Menu Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-3 rounded-xl bg-black/30 backdrop-blur-sm border border-neon-green/30 hover:border-neon-green/50 hover:bg-black/50 transition-all duration-300 shadow-lg hover:shadow-neon-green/20"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-neon-green" />
              ) : (
                <Menu className="w-6 h-6 text-neon-green" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/98 backdrop-blur-xl border-t border-neon-green/20 shadow-2xl"
          >
            <nav className="container mx-auto px-4 py-6">
              <div className="flex flex-col space-y-4">
                {navItems.map((item, index) => (
                  <motion.a
                    key={index}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item.href);
                    }}
                    className="text-white/90 hover:text-neon-green transition-all duration-300 py-3 px-4 border-b border-neon-green/10 last:border-b-0 hover:bg-neon-green/5 rounded-lg font-medium"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.label}
                  </motion.a>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header; 